import React, { useState, useEffect, useMemo } from 'react';
import { logger, LogLevel } from '@/shared/utils/logger';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { ScrollArea } from '@/shared/ui/scroll-area';
import { Badge } from '@/shared/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface DebugLoggerProps {
  isOpen: boolean;
  onClose: () => void;
}

const DebugLogger: React.FC<DebugLoggerProps> = ({ isOpen, onClose }) => {
  const [logs, setLogs] = useState<any[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('all');
  const [selectedSource, setSelectedSource] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;

    const updateLogs = () => {
      const allLogs = logger.getLogs();
      setLogs([...allLogs].reverse()); // Show newest first
    };

    // Update logs immediately
    updateLogs();

    // Update logs every second when visible
    const interval = setInterval(updateLogs, 1000);
    return () => clearInterval(interval);
  }, [isOpen]);

  // Get unique sources from logs
  const availableSources = useMemo(() => {
    const sources = new Set<string>();
    logs.forEach(log => {
      if (log.source) {
        sources.add(log.source);
      }
    });
    return Array.from(sources).sort((a, b) => a.localeCompare(b));
  }, [logs]);

  // Filter logs based on selected criteria
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // Filter by level
      if (selectedLevel !== 'all' && log.level !== selectedLevel) {
        return false;
      }

      // Filter by source
      if (selectedSource !== 'all' && log.source !== selectedSource) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const messageMatch = log.message.toLowerCase().includes(searchLower);
        const sourceMatch = log.source?.toLowerCase().includes(searchLower);
        const dataMatch = JSON.stringify(log.data).toLowerCase().includes(searchLower);

        if (!messageMatch && !sourceMatch && !dataMatch) {
          return false;
        }
      }

      return true;
    });
  }, [logs, selectedLevel, selectedSource, searchTerm]);

  const clearLogs = () => {
    logger.clearLogs();
    setLogs([]);
  };

  const exportLogs = () => {
    const logData = logger.exportLogs();
    const blob = new Blob([logData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${new Date().toISOString()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setSelectedLevel('all');
    setSelectedSource('all');
    setSearchTerm('');
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-[95vw] h-[95vh] max-w-6xl">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between mb-4">
            <CardTitle>Debug Logger ({filteredLogs.length} of {logs.length} logs)</CardTitle>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={exportLogs}>
                Export
              </Button>
              <Button size="sm" variant="outline" onClick={clearLogs}>
                Clear
              </Button>
              <Button size="sm" variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Level Filter */}
            <div className="space-y-2">
              <Label htmlFor="level-filter" className="text-sm font-medium">Level</Label>
              <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                <SelectTrigger id="level-filter">
                  <SelectValue placeholder="All Levels" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value={LogLevel.DEBUG}>Debug</SelectItem>
                  <SelectItem value={LogLevel.INFO}>Info</SelectItem>
                  <SelectItem value={LogLevel.WARN}>Warn</SelectItem>
                  <SelectItem value={LogLevel.ERROR}>Error</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Source Filter */}
            <div className="space-y-2">
              <Label htmlFor="source-filter" className="text-sm font-medium">Source</Label>
              <Select value={selectedSource} onValueChange={setSelectedSource}>
                <SelectTrigger id="source-filter">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {availableSources.map(source => (
                    <SelectItem key={source} value={source}>
                      {source}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search Filter */}
            <div className="space-y-2">
              <Label htmlFor="search-filter" className="text-sm font-medium">Search</Label>
              <Input
                id="search-filter"
                placeholder="Search in message, source, or data..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Clear Filters */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">&nbsp;</Label>
              <Button
                size="sm"
                variant="outline"
                onClick={clearFilters}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0 h-full">
          <ScrollArea className="h-[calc(95vh-200px)]">
            <div className="space-y-2">
              {filteredLogs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm">
                    {logs.length === 0 ? 'No logs yet' : 'No logs match the current filters'}
                  </p>
                  {logs.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={clearFilters}
                      className="mt-2"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              ) : (
                filteredLogs.map((log, index) => (
                  <div key={index} className="p-3 border rounded-md bg-background">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge
                        variant={log.level === LogLevel.ERROR ? 'destructive' :
                          log.level === LogLevel.WARN ? 'secondary' :
                            log.level === LogLevel.INFO ? 'default' :
                              log.level === LogLevel.SUCCESS ? 'default' : 'outline'}
                        className={`text-xs ${log.level === LogLevel.SUCCESS ? 'bg-green-500' : ''}`}
                      >
                        {log.level}
                      </Badge>
                      {log.source && (
                        <Badge variant="secondary" className="text-xs">
                          {log.source}
                        </Badge>
                      )}
                      <span className="text-sm">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="font-mono text-sm">{log.message}</div>
                    {log.data && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-sm">
                          Data
                        </summary>
                        <pre className="text-xs bg-muted p-2 rounded mt-1 overflow-x-auto">
                          {JSON.stringify(log.data, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default DebugLogger; 